import { createCheckoutSession } from "@/api/privateRequests/stripe/createCheckoutSession";
import Button from "@/components/Button";
import { arrow } from "@/designSystemIcons";
import { useEnter, useEscape } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { login, loginWithEmail } from "../../../../api/publicApi";
import { useGoogleLogin } from "../../../../hooks/useGoogleLogin";
import { showModal } from "../../../../redux/message/messageSlice";
import { fetchSbcsSets } from "../../../../redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "../../../../redux/store";
import { buyIntentSelector, fetchUser } from "../../../../redux/user/userSlice";
import InputField from "../../../InputField";

type SaveSquadRequestType = {
  name?: string;
  formation: string;
  players: {
    playerRoleId: number;
    resourceId: number;
    evoPathId?: number;
  }[];
  tacticId?: string;
  manager: {
    countryId: number;
    leagueId: number;
  };
  tactic?: {
    name: string;
    description: string;
    logoUrl: string;
    teamTactics: {
      buildUpStyle: string;
      formationId: string;
      defensiveApproach: string;
      lineHeight: number;
    };
    playerRoles: {
      playerRoleId: number;
      focus?: string;
    }[];
  };
};
interface Props {
  onClose: () => void;
  type: "login" | "sign-in-to-subscribe";
  handleLoginSuccess?: (method: "email" | "google") => void;
  squad?: SaveSquadRequestType;
}

interface LocationState {
  location: Location;
  buyIntent: string;
  discountId?: string;
}

export const LoginSection = ({
  onClose,
  type,
  handleLoginSuccess,
  squad,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [continueClicked, setContinueClicked] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [showEmailInvalid, setShowEmailInvalid] = useState(false);
  // const locationState = useLocation().state as LocationState;
  const [clickedResend, setClickedResend] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const googleButtonWidth = useMemo(() => (isMobile ? 290 : 330), [isMobile]);
  const buyIntent = useSelector(buyIntentSelector);

  useEscape(onClose);

  const handleContinue = () => {
    if (isValidEmail) {
      setContinueClicked(true);
      loginWithEmail(
        email,
        type === "sign-in-to-subscribe" ? buyIntent?.subscriptionId : undefined,
        type === "sign-in-to-subscribe" ? buyIntent?.discountId : undefined,
        squad ?? undefined,
      );
      handleLoginSuccess?.("email");
    } else {
      setShowEmailInvalid(true);
    }
  };

  useEnter(handleContinue);

  const onLoginSuccess = useCallback(async () => {
    await dispatch(fetchUser({ didLogin: true }));
    if (buyIntent) {
      createCheckoutSession({
        priceId: buyIntent.subscriptionId,
        discountId: buyIntent.discountId,
      });
    }
    dispatch(fetchSbcsSets());
    dispatch(showModal());
    handleLoginSuccess?.("google");
  }, [dispatch]);

  useGoogleLogin({
    onLogin: login,
    onSuccess: onLoginSuccess,
    onFailure: setError,
    googleButtonRef: googleButtonRef,
    buttonWidth: googleButtonWidth, // Integer value to prevent flickering
  });

  const handleEmailChange = (value: string) => {
    setIsValidEmail(emailRegex.test(value));
    setShowEmailInvalid(false);
    setEmail(value);
  };

  // Re-render Google button when returning from continueClicked view
  useEffect(() => {
    if (!continueClicked && window.google && googleButtonRef.current) {
      // Clear the container first
      googleButtonRef.current.innerHTML = "";
      // Re-render the Google button
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        type: "standard",
        text: "continue_with",
        width: googleButtonWidth,
      });
    }
  }, [continueClicked]);

  return (
    <section className="flex flex-col mt-10 gap-y-4 h-full pb-6">
      {continueClicked ? (
        <div className="flex flex-col gap-y-1 h-full">
          <button
            className="absolute top-4 left-4 text-dark-tertiary w-3 h-3 rotate-90"
            onClick={(e) => {
              e.stopPropagation();
              setContinueClicked(false);
              setClickedResend(false);
            }}
          >
            {arrow}
          </button>
          <p className="text-sm text-complete">
            Success. Check your email for a link
          </p>
          <section className="flex flex-col gap-y-3 py-6 px-3 bg-popup-inner-box rounded-sm">
            <p>We've sent a login link to</p>
            <p className="text-complete">{email}</p>
          </section>
          <section className="mt-auto text-xs text-dark-tertiary">
            Link expires in 30 minutes. Didn't receive email? Check your spam
            folder or{" "}
            <button
              className={cn(
                clickedResend
                  ? "text-complete cursor-default"
                  : "text-primary-action underline",
              )}
              onClick={() => {
                if (clickedResend) return;
                loginWithEmail(
                  email,
                  type === "sign-in-to-subscribe"
                    ? buyIntent?.subscriptionId
                    : undefined,
                  type === "sign-in-to-subscribe"
                    ? buyIntent?.discountId
                    : undefined,
                );
                setClickedResend(true);
              }}
            >
              resend
            </button>
          </section>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-y-1">
            <p className="text-sm text-dark-secondary">Log in with email:</p>
            <InputField
              type="email"
              value={email}
              placeholder="your@email.com"
              onChange={handleEmailChange}
              dismissSearch={() => {}}
            />
            {showEmailInvalid && (
              <div className="mx-auto text-sm text-error">
                please provide a valid email
              </div>
            )}
          </div>
          <Button
            label="Continue"
            onClick={(e) => {
              e.stopPropagation();
              handleContinue();
            }}
            variant="primary"
            large
          />
          <div className="flex flex-row items-center w-full gap-x-2 my-6">
            <div className="h-[1px] w-full bg-dark-secondary" />
            <div className="text-sm text-dark-secondary">OR</div>
            <div className="h-[1px] w-full bg-dark-secondary" />
          </div>
          <div className="flex flex-col">
            <div
              ref={googleButtonRef}
              className="w-[300px] h-[44px] mx-auto"
              style={{
                minHeight: "44px", // Ensure minimum height to prevent layout shift
                minWidth: googleButtonWidth, // Ensure minimum width to prevent layout shift
              }}
            />
            {error && (
              <div className="mx-auto text-sm text-error w-full mt-1">
                {error}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};
